// Scene action helpers — mirror the mobile app's scene domain so the web
// builder speaks the same language and produces the same on-hub shape:
//   { type: 'device', device, parentId, payload: { <property>: <typed value> } }
// Existing scenes (gang payloads like { state_1: 'ON' }) round-trip unchanged.

// Device types a scene can actuate. Sensors expose config "controls"
// (radar_sensitivity, ranges…) but aren't scene-settable; routers/extenders and
// BLE locks are out too.
const ACTUATABLE_TYPES = ['light', 'switch', 'thermostat', 'curtain'];

// Controls that are device CONFIGURATION, not scene-settable state — kept out of
// scene actions (a scene shouldn't flip a curtain's motor direction).
const CONFIG_PROPERTIES = new Set([
  'motor_direction', 'linkquality',
  'radar_sensitivity', 'minimum_range', 'maximum_range', 'detection_delay', 'fading_time',
]);

/**
 * The scene-settable controls for a device: actuation only (no config), and
 * de-duplicated by property (some converters, e.g. the rotary AC, expose
 * `fan_mode` twice; th4 may not have the backend de-dup yet).
 */
export function sceneControls(device) {
  const seen = new Set();
  return (device?.controls || []).filter((c) => {
    if (!c.property || CONFIG_PROPERTIES.has(c.property) || seen.has(c.property)) return false;
    seen.add(c.property);
    return true;
  });
}

/** Devices a scene can actuate. */
export function isActuatable(device) {
  const type = (device?.type || '').toLowerCase();
  if (!ACTUATABLE_TYPES.includes(type)) return false;
  if ((device.controls || []).some(c => c.property === 'lock_state')) return false;
  return sceneControls(device).length > 0;
}

/** The control that best represents a device's primary state for a scene. */
function primaryControl(controls) {
  return (
    controls.find(c => c.valueType === 'binary') ||
    controls.find(c => c.property?.includes('current_heating_setpoint')) ||
    controls.find(c => c.property === 'position') ||
    controls[0] ||
    null
  );
}

/**
 * Seed a device action from the device's CURRENT live state — the "copy how
 * things are now" default. Emits ONE clean key (the primary control), so a
 * light is { state_1: 'ON' } and a curtain is { position: 98 } — not the whole
 * control set. Binary controls are emitted as on/off STRINGS, never booleans.
 */
export function buildActionFromLiveState(device) {
  const control = primaryControl(sceneControls(device));
  const payload = {};
  if (control && control.value !== undefined && control.value !== null) {
    payload[control.property] = control.valueType === 'binary'
      ? (isOnValue(control.value, control) ? (control.options?.on ?? 'ON') : (control.options?.off ?? 'OFF'))
      : control.value;
  }
  return {
    type: 'device',
    device: device.id,
    parentId: device.parentId || device.id,
    payload,
  };
}

/**
 * Resolve the device an action targets. Handles the legacy shape where
 * `action.device` is the PARENT id (e.g. a multi-gang switch) while the device
 * list only holds the per-gang CHILD — matched by parentId + the payload's
 * control key (state_1 → the child exposing state_1).
 */
export function resolveActionDevice(action, devices) {
  if (!action || action.type !== 'device') return null;
  const list = devices || [];
  const direct = list.find((d) => d.id === action.device);
  if (direct) return direct;
  const keys = Object.keys(action.payload || {});
  return (
    list.find(
      (d) =>
        (d.parentId === action.device || d.parentId === action.parentId) &&
        (d.controls || []).some((c) => keys.includes(c.property)),
    ) || null
  );
}

/**
 * Normalize a device action to the canonical child-id + parentId shape (the same
 * shape new actions and many existing scenes already use). Applied on save so a
 * legacy parent-id scene self-heals the moment it's edited — no bulk migration.
 * Unresolvable actions (deleted device) are left untouched so nothing is lost.
 */
export function normalizeAction(action, devices) {
  if (!action || action.type !== 'device') return action;
  const device = resolveActionDevice(action, devices);
  if (!device) return action;
  return { ...action, device: device.id, parentId: device.parentId || device.id };
}

function isOnValue(val, control) {
  return val === true || val === 'ON' || (control && val === control.options?.on);
}

function formatScalar(control, val) {
  if (control?.valueType === 'binary') return isOnValue(val, control) ? 'on' : 'off';
  const unit = control?.options?.unit || control?.unit || '';
  return `${val}${unit}`;
}

/** Human-readable one-liner for an action row, e.g. "Turn on Wall Light". */
export function describeSceneAction(action, devices) {
  if (!action) return '';
  if (action.type === 'scene') return 'Activate a scene';
  if (action.type === 'notification') return `Notify: ${action.message || ''}`;
  if (action.type !== 'device') return action.type;

  const device = resolveActionDevice(action, devices);
  const name = device?.name || device?.friendly_name || 'Device';
  const entries = Object.entries(action.payload || {});
  if (entries.length === 0) return name;

  const controlFor = (key) => device?.controls?.find(c => c.property === key);

  if (entries.length === 1) {
    const [key, val] = entries[0];
    const control = controlFor(key);
    const binary = control?.valueType === 'binary' || val === 'ON' || val === 'OFF' || val === true || val === false;
    if (binary) return `Turn ${isOnValue(val, control) ? 'on' : 'off'} ${name}`;
    return `Set ${name} to ${formatScalar(control, val)}`;
  }

  const parts = entries.map(([key, val]) => {
    const control = controlFor(key);
    const label = control?.name || key;
    return `${label} ${formatScalar(control, val)}`;
  });
  return `${name}: ${parts.join(', ')}`;
}

/** Just the target STATE, no device name — for UIs that show the name already. */
export function describeActionState(action, device) {
  const entries = Object.entries(action?.payload || {});
  if (entries.length === 0) return '';
  const controlFor = (key) => device?.controls?.find((c) => c.property === key);

  if (entries.length === 1) {
    const [key, val] = entries[0];
    const control = controlFor(key);
    const binary = control?.valueType === 'binary' || val === 'ON' || val === 'OFF' || typeof val === 'boolean';
    if (binary) return isOnValue(val, control) ? 'On' : 'Off';
    return formatScalar(control, val);
  }

  return entries
    .map(([key, val]) => {
      const control = controlFor(key);
      return `${control?.name || key} ${formatScalar(control, val)}`;
    })
    .join(', ');
}
